import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PRIORITY_COLORS } from '../lib/constants';
import api from '../lib/api';

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

// Simple Gantt-style timeline. Range = 21 days centered on today (or cursor).
export default function TimelineView({ tasks, onOpenTask, onUpdate }) {
  const [cursor, setCursor] = useState(startOfDay(new Date()));
  const [dragState, setDragState] = useState(null); // { taskId, offsetDays }
  const DAYS = 21;
  const startDate = useMemo(() => addDays(cursor, -3), [cursor]);
  const cellWidth = 44;

  const days = useMemo(() => {
    return Array.from({ length: DAYS }, (_, i) => addDays(startDate, i));
  }, [startDate]);

  // For each task, compute [startCol, endCol] in the range. Use created_at -> due_date.
  const rows = useMemo(() => {
    const startMs = startDate.getTime();
    const endMs = addDays(startDate, DAYS).getTime();
    return tasks.filter((t) => t.due_date).map((t) => {
      const isDraggingThis = dragState && dragState.taskId === t.id;
      const offsetDays = isDraggingThis ? dragState.offsetDays : 0;

      const dueMs = new Date(t.due_date).getTime() + (offsetDays * 86400000);
      const createdMs = startOfDay(new Date(t.created_at || t.due_date)).getTime() + (offsetDays * 86400000);
      const barStart = Math.max(createdMs, startMs);
      const barEnd = Math.min(dueMs + 86400000, endMs); // include the due day
      const visible = barEnd > barStart;
      const leftDays = Math.max(0, Math.round((barStart - startMs) / 86400000));
      const widthDays = Math.max(1, Math.round((barEnd - barStart) / 86400000));
      return { ...t, visible, leftDays, widthDays };
    }).filter((t) => t.visible).sort((a, b) => a.leftDays - b.leftDays);
  }, [tasks, startDate, dragState]);

  const startDrag = (e, task) => {
    e.preventDefault();
    const startX = e.clientX;
    let finalOffsetDays = 0;
    
    const doDrag = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      finalOffsetDays = Math.round(deltaX / cellWidth);
      setDragState({ taskId: task.id, offsetDays: finalOffsetDays });
    };
    
    const stopDrag = async () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
      
      setDragState(null);
      if (finalOffsetDays !== 0) {
        const currentDueDate = new Date(task.due_date);
        const newDueDate = addDays(currentDueDate, finalOffsetDays);
        
        try {
          await api.patch(`/tasks/${task.id}`, { due_date: newDueDate.toISOString() });
          if (onUpdate) onUpdate();
        } catch (err) {
          console.error('Failed to reschedule task', err);
        }
      }
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  return (
    <div style={{ padding: 24 }} data-testid="timeline-view">
      <div className="flex items-center gap-4 mb-4" style={{ color: '#fff' }}>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(addDays(cursor, -7))} data-testid="tl-prev"><ChevronLeft size={16} /></button>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>
          {startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — {addDays(startDate, DAYS - 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </h2>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(addDays(cursor, 7))} data-testid="tl-next"><ChevronRight size={16} /></button>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(startOfDay(new Date()))}>Today</button>
      </div>
      <div style={{ background: 'var(--trello-card-bg)', borderRadius: 8, overflowX: 'auto' }}>
        {/* header */}
        <div style={{ display: 'grid', gridTemplateColumns: `220px repeat(${DAYS}, ${cellWidth}px)`, background: 'var(--trello-list-bg)', borderBottom: '1px solid #DFE1E6' }}>
          <div style={{ padding: 8, fontSize: 12, fontWeight: 700, color: 'var(--trello-text-muted)' }}>Task</div>
          {days.map((d, i) => {
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <div key={i} style={{ padding: '6px 4px', textAlign: 'center', fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? '#0079BF' : 'var(--trello-text-muted)', background: isToday ? '#DEEBFF' : 'transparent' }}>
                <div>{d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2)}</div>
                <div>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* rows */}
        {rows.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--trello-text-muted)' }}>No tasks with due dates in this range.</div>}
        {rows.map((t) => (
          <div key={t.id} style={{ display: 'grid', gridTemplateColumns: `220px repeat(${DAYS}, ${cellWidth}px)`, borderBottom: '1px solid #F4F5F7', alignItems: 'center', minHeight: 40 }} data-testid={`tl-row-${t.id}`}>
            <div style={{ padding: '6px 8px', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.completed ? '✓ ' : ''}{t.title}
            </div>
            <div style={{ gridColumn: `${2 + t.leftDays} / span ${t.widthDays}`, position: 'relative', paddingRight: 2 }}>
              <button 
                onClick={() => onOpenTask(t.id)} 
                onMouseDown={(e) => startDrag(e, t)}
                data-testid={`tl-bar-${t.id}`} 
                title="Drag left/right to reschedule task start/due dates"
                style={{
                  width: '100%', height: 24, border: 'none', cursor: 'grab',
                  background: t.label_color || PRIORITY_COLORS[t.priority] || '#0079BF',
                  color: '#fff', fontSize: 11, fontWeight: 600, padding: '0 6px', textAlign: 'left',
                  borderRadius: 4, opacity: t.completed ? 0.6 : 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  userSelect: 'none',
                }}
              >{t.title}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
